import tracker from "../../../helpers/tracker";
import { useStoreId } from "../../../core/auth/useLoggedInUser";
import * as mfaqRepository from "../BackendApiRepository/MfaqRepository";
import { aggregateVotes } from "../helper";
import { useQuery } from "@tanstack/react-query";

const getCalendarDate = (timestamp) => {
  if (timestamp == null) return undefined;

  const date = new Date(timestamp);
  return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map((value) => String(value).padStart(2, "0"))
    .join("-");
};

export function getQuestionsQueryKey(
  store_id,
  area,
  since,
  until,
  from,
  onlyQuestionsWithTasks,
  v2PendingQuestionIds
) {
  return [
    "questions",
    store_id,
    area,
    getCalendarDate(since),
    getCalendarDate(until),
    from,
    onlyQuestionsWithTasks,
    v2PendingQuestionIds ? [...v2PendingQuestionIds].sort() : undefined,
  ];
}

export function getQuestionQueryKey(question_id, since, until) {
  return [
    "question",
    question_id,
    getCalendarDate(since),
    getCalendarDate(until),
  ];
}

export function getVotesQueryKey(
  question_id,
  area,
  startDate,
  endDate,
  isWholeStore
) {
  return [
    "votes",
    question_id,
    area,
    getCalendarDate(startDate),
    getCalendarDate(endDate),
    isWholeStore,
  ];
}

export function useGetOtherQuestions(
  since,
  until,
  area,
  from,
  onlyQuestionsWithTasks,
  v2PendingQuestionIds
) {
  const store_id = useStoreId();

  const {
    data: questions,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: getQuestionsQueryKey(
      store_id,
      area,
      since,
      until,
      from,
      onlyQuestionsWithTasks,
      v2PendingQuestionIds
    ),
    queryFn: () =>
      mfaqRepository.getAllQuestions(
        store_id,
        area,
        since,
        until,
        from,
        onlyQuestionsWithTasks,
        v2PendingQuestionIds
      ),
    staleTime: 0, // No stale time here since newly added questions should be shown immediately
    retry: 1, // retry once
  });

  return { data: questions, isLoading, isError, error };
}

export function useGetTopQuestions(
  since,
  until,
  area,
  onlyQuestionsWithTasks,
  v2PendingQuestionIds
) {
  const store_id = useStoreId();

  const {
    data: questions,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: getQuestionsQueryKey(
      store_id,
      area,
      since,
      until,
      0,
      onlyQuestionsWithTasks,
      v2PendingQuestionIds
    ),
    queryFn: () =>
      mfaqRepository.getAllQuestions(
        store_id,
        area,
        since,
        until,
        0,
        onlyQuestionsWithTasks,
        v2PendingQuestionIds
      ),

    staleTime: 0,
    retry: 1,
  });

  return { data: questions, loading: isLoading, isError, error };
}

export function useMFAQ(question_id, since, until) {
  const {
    data: question,
    isLoading,
    isError,
  } = useQuery({
    queryKey: getQuestionQueryKey(question_id, since, until),
    queryFn: () => {
      if (!question_id) return Promise.resolve(undefined);
      return mfaqRepository.getQuestionById(question_id, since, until);
    },
    enabled: !!question_id,
    staleTime: 0,
  });
  return { data: question, isLoading, isError };
}

/**
 * @param {string} searchTerm
 * @param {string?} questionType
 * @param {string?} area
 * @returns {Promise<any[]>}
 */
export async function searchMFAQ(
  searchTerm,
  questionType,
  area,
  store_id,
  from
) {
  return await mfaqRepository.searchQuestions(searchTerm, store_id, area, from);
}

export async function searchMFAQWithSearchTerm(
  searchTerm,
  questionType,
  area,
  store_id,
  from
) {
  const matchingQuestions = await searchMFAQ(
    searchTerm,
    questionType,
    area,
    store_id,
    from
  );
  return { matchingQuestions, searchTermSearched: searchTerm };
}

export async function createVote(question_id, value, vote_location, store_id) {
  const result = mfaqRepository.createVote(
    question_id,
    store_id,
    vote_location,
    value
  );
  tracker.MFAQ.trackVote(question_id, store_id, value, vote_location);
  return result.data;
}

export async function updateQuestionRequest(questionId, questionText) {
  await mfaqRepository.updateQuestion(questionId, questionText);
}

export async function mergeQuestionRequest(questionId, secondQuestionId) {
  return await mfaqRepository.mergeQuestions(questionId, secondQuestionId);
}

export async function createQuestion(storeId, area, questionText) {
  await mfaqRepository.createQuestion(storeId, area, questionText);
}

export function useGetAggregatedVotes(
  question_id,
  interval,
  startDate,
  endDate,
  area,
  isWholeStore
) {
  const fetchVotes = () =>
    mfaqRepository.getVotesByQuestionById(
      question_id,
      area,
      startDate,
      endDate,
      isWholeStore
    );

  const { data: votes, isLoading } = useQuery({
    queryKey: getVotesQueryKey(
      question_id,
      area,
      startDate,
      endDate,
      isWholeStore
    ),
    queryFn: fetchVotes,
    staleTime: 0,
  });

  const data = votes ? aggregateVotes(votes, interval) : [];

  return { loading: isLoading, data };
}
